"use client"

import * as React from "react"
import { X, Check, Trash2 } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { SupabaseUpload } from "@/components/SupabaseUpload"
import { UserProfile } from "@/types/workspace"
import { toast } from "sonner"

interface WallpaperModalProps {
  isOpen: boolean
  onClose: () => void
  user: UserProfile | null
  desktopWallpaper: string
  setDesktopWallpaper: (url: string) => void
  mobileWallpaper: string
  setMobileWallpaper: (url: string) => void
  useSeparateMobileWallpaper: boolean
  setUseSeparateMobileWallpaper: (val: boolean) => void
  wallpaperTab: "desktop" | "mobile"
  setWallpaperTab: (tab: "desktop" | "mobile") => void
  customWallpapers: string[]
  setCustomWallpapers: (urls: string[]) => void
  handleSaveWallpaperToDb: (d: string, m: string, sep: boolean) => void
}

export function WallpaperModal({
  isOpen,
  onClose,
  user,
  desktopWallpaper,
  setDesktopWallpaper,
  mobileWallpaper,
  setMobileWallpaper,
  useSeparateMobileWallpaper,
  setUseSeparateMobileWallpaper,
  wallpaperTab,
  setWallpaperTab,
  customWallpapers,
  setCustomWallpapers,
  handleSaveWallpaperToDb,
}: WallpaperModalProps) {
  if (!isOpen) return null

  const activeWallpaper = wallpaperTab === "mobile" ? mobileWallpaper : desktopWallpaper

  const handleSetWallpaper = (v: string) => {
    if (user && v.startsWith("http") && !customWallpapers.includes(v)) {
      const updated = [v, ...customWallpapers]
      setCustomWallpapers(updated)
      localStorage.setItem(`customWallpapers:${user.id}`, JSON.stringify(updated))
    }
    if (wallpaperTab === "mobile") {
      setMobileWallpaper(v)
      if (user) {
        localStorage.setItem(`mobileWallpaper:${user.id}`, v)
        handleSaveWallpaperToDb(desktopWallpaper, v, useSeparateMobileWallpaper)
      }
    } else {
      setDesktopWallpaper(v)
      if (user) {
        localStorage.setItem(`desktopWallpaper:${user.id}`, v)
        handleSaveWallpaperToDb(v, mobileWallpaper, useSeparateMobileWallpaper)
      }
    }
  }

  const systemPresetIds = [
    "bold-tech",
    "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1600",
    "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1600"
  ]

  const uniqueCustoms = Array.from(new Set(customWallpapers)).filter(
    (url) => !systemPresetIds.includes(url)
  )

  const customPresets = uniqueCustoms.map((url, index) => ({
    id: url,
    name: `Enviado ${index + 1}`,
    preview: "url",
    url: url,
    isCustom: true,
  }))

  const handleRemoveCustomWallpaper = (e: React.MouseEvent, urlToRemove: string) => {
    e.stopPropagation()
    const updated = customWallpapers.filter((u) => u !== urlToRemove)
    setCustomWallpapers(updated)
    if (user) {
      localStorage.setItem(`customWallpapers:${user.id}`, JSON.stringify(updated))
    }
    if (desktopWallpaper === urlToRemove) {
      handleSetWallpaper("bold-tech")
    }
    if (mobileWallpaper === urlToRemove) {
      setMobileWallpaper("bold-tech")
    }
    toast.success("Imagem removida da sua lista.")
  }

  const presets = [
    { id: "bold-tech", name: "Bold Workspace (Padrão)", preview: "url", url: "/bold-tech-wallpaper.png", isCustom: false },
    { id: "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1600", name: "Gradiente Abstrato", preview: "url", url: "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=300", isCustom: false },
    { id: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1600", name: "Glassmorphism 3D", preview: "url", url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=300", isCustom: false },
    ...customPresets,
  ]

  return (
    <div className="fixed inset-0 z-55 flex items-center justify-center bg-black/60 backdrop-blur-xs font-sans p-4">
      <div className="w-full max-w-lg max-h-[90vh] flex flex-col border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-2xl rounded-2xl overflow-hidden p-0">
        <div className="flex justify-between items-center p-5 pb-3 border-b border-zinc-150 dark:border-zinc-850/80 shrink-0">
          <h3 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
            <span>🖼️</span> Definir Plano de Fundo
          </h3>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-650 dark:hover:text-zinc-250 cursor-pointer p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="p-5 pb-8 overflow-y-auto flex-1 space-y-4 scrollbar-thin">
          <div className="flex rounded-xl bg-zinc-100 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 p-1 gap-1">
            <button
              type="button"
              onClick={() => setWallpaperTab("desktop")}
              className={`flex-1 text-xs font-bold py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-2 ${wallpaperTab === "desktop"
                  ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-sm"
                  : "text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                }`}
            >
              <span>🖥️</span> Computador
            </button>
            <button
              type="button"
              onClick={() => setWallpaperTab("mobile")}
              className={`flex-1 text-xs font-bold py-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-2 ${wallpaperTab === "mobile"
                  ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-sm"
                  : "text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                }`}
            >
              <span>📱</span> Celular
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {presets.map((preset) => {
              const isSelected = activeWallpaper === preset.id
              return (
                <div
                  key={preset.id}
                  onClick={() => handleSetWallpaper(preset.id)}
                  className={`group relative rounded-xl h-24 overflow-hidden border-2 cursor-pointer transition-all duration-200 shadow-sm ${isSelected
                      ? "border-[#004EB1] ring-2 ring-[#004EB1]/30 scale-[1.02]"
                      : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-600"
                    }`}
                >
                  {preset.preview === "url" ? (
                    <img src={(preset as any).url} alt={preset.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full ${preset.preview}`} />
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-2 text-center">
                    <span className="text-white text-xs font-bold">{preset.name}</span>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2 bg-[#004EB1] text-white p-1 rounded-full shadow-md z-10">
                      <Check className="size-3" />
                    </div>
                  )}
                  {preset.isCustom && (
                    <button
                      type="button"
                      onClick={(e) => handleRemoveCustomWallpaper(e, preset.id)}
                      className="absolute top-2 left-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full shadow-md z-10 opacity-80 hover:opacity-100 transition-opacity"
                      title="Remover Imagem"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          <div className="pt-3 border-t border-zinc-150 dark:border-zinc-800 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-zinc-700 dark:text-zinc-300">Enviar Imagem Personalizada</p>
              <p className="text-[10px] text-zinc-400">Proporção 16:9 (1080p Desktop)</p>
            </div>
            <SupabaseUpload
              onUploadSuccess={(url: string) => handleSetWallpaper(url)}
              accept="image/*"
              label="Enviar Imagem"
              mode="button"
              cropShape="rect"
              aspectRatio={16 / 9}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-zinc-150 dark:border-zinc-850/80 p-5 pt-3 shrink-0">
          <Button
            onClick={() => {
              onClose()
              toast.success("Plano de fundo atualizado.")
            }}
            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold py-1.5 px-4 cursor-pointer"
          >
            Concluir
          </Button>
        </div>
      </div>
    </div>
  )
}
